import { useCallback, useMemo } from 'react';
import { Select, Text } from '@mantine/core';
import type { ComboboxParsedItem, OptionsFilter } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { CustomerSwitchNav } from '@/shared/utils/customerSwitchNav';
import {
  useUploadStore,
  getInFlightUploadCount,
  getProcessingCount,
} from '@/features/upload/uploadStore';
import { useMergeTrackingStore } from '@/features/media/stores/mergeTrackingStore';
import { bcInt } from '@lib2/model/util/string/BaseConverter';

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

const optionsFilter: OptionsFilter = ({ options, search }) => {
  const terms = search.toLowerCase().trim().split(' ').filter(Boolean);
  if (terms.length === 0) return options;

  return options.reduce<ComboboxParsedItem[]>((acc, item) => {
    if ('group' in item) {
      const filtered = item.items.filter((opt) => {
        const words = opt.label.toLowerCase().split(' ');
        return terms.every((t) => words.some((w) => w.includes(t)));
      });
      if (filtered.length > 0) {
        acc.push({ group: item.group, items: filtered });
      }
    } else {
      const words = item.label.toLowerCase().split(' ');
      if (terms.every((t) => words.some((w) => w.includes(t)))) {
        acc.push(item);
      }
    }
    return acc;
  }, []);
};

export function CustomerSelector() {
  const { customerInfo, selectedCId, setSelectedCId, canAccessSubAccounts, selectedCustomer } = useAuth();
  const reset = useUploadStore((s) => s.reset);
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });

  const handleChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      const newCId = parseInt(value, 10);
      if (newCId === selectedCId) return;

      // Clears leftover upload items (they belong to the account being left)
      // and, when server-side processing continues unattended, says so —
      // one toast per source account so repeated switches update, not stack.
      const switchTo = () => {
        const processingCount = getProcessingCount();
        if (useUploadStore.getState().items.length > 0) {
          reset();
        }
        if (processingCount > 0 && selectedCId != null) {
          const label = selectedCustomer?.cfg.label ?? 'the previous account';
          const toastId = `upload-processing-continues-${selectedCId}`;
          notifications.hide(toastId);
          notifications.show({
            id: toastId,
            title: 'Processing continues',
            message:
              processingCount === 1
                ? `1 video from ${label} is still processing — it will appear in that account's library when done.`
                : `${processingCount} videos from ${label} are still processing — they will appear in that account's library when done.`,
            color: 'blue',
            autoClose: 6000,
          });
        }
        // Merge jobs of the account being left can't be observed under the
        // new account's job poll — their "Merging..." toasts would spin
        // forever. Drop tracking + toasts and say the work continues
        // server-side (same contract as processing uploads above).
        const { pendingMerges } = useMergeTrackingStore.getState();
        if (pendingMerges.length > 0 && selectedCId != null) {
          useMergeTrackingStore.getState().clearWithToasts();
          const label = selectedCustomer?.cfg.label ?? 'the previous account';
          const mergeToastId = `merge-processing-continues-${selectedCId}`;
          notifications.hide(mergeToastId);
          notifications.show({
            id: mergeToastId,
            title: 'Merges continue',
            message:
              pendingMerges.length === 1
                ? `1 merge from ${label} is still processing — the merged video will appear in that account's library when done.`
                : `${pendingMerges.length} merges from ${label} are still processing — the merged videos will appear in that account's library when done.`,
            color: 'blue',
            autoClose: 6000,
          });
        }
        // Entity-detail routes address the account being left — land on the
        // section list before the cId flips so the new account's data loads
        // into a route that exists for it.
        const redirectPath = CustomerSwitchNav.getRedirectPath(pathname);
        if (redirectPath) {
          navigate({ to: redirectPath });
        }
        setSelectedCId(newCId);
      };

      // Only uploads the client is still sending need confirmation; items in
      // server-side processing are unaffected by a switch.
      const inFlightCount = getInFlightUploadCount();
      if (inFlightCount > 0) {
        modals.openConfirmModal({
          title: 'Cancel active uploads?',
          children: (
            <Text size="sm">
              You have {inFlightCount} upload{inFlightCount !== 1 ? 's' : ''} in progress.
              Switching accounts will cancel {inFlightCount !== 1 ? 'them' : 'it'}.
            </Text>
          ),
          labels: { confirm: 'Switch account', cancel: 'Keep uploading' },
          confirmProps: { color: 'red' },
          onConfirm: switchTo,
        });
        return;
      }

      switchTo();
    },
    [selectedCId, setSelectedCId, reset, selectedCustomer, pathname, navigate]
  );

  const data = useMemo(() => {
    if (!customerInfo) return [];

    const sortedSubs = [...customerInfo.subcustomers].sort((a, b) =>
      collator.compare(a.cfg.label, b.cfg.label)
    );

    return [
      {
        group: 'Parent Account',
        items: [{
          value: String(customerInfo.customer.cfg.cId),
          label: customerInfo.customer.cfg.label,
        }],
      },
      {
        group: `Sub-Accounts (${sortedSubs.length})`,
        items: sortedSubs.map(sub => ({
          value: String(sub.cfg.cId),
          label: sub.cfg.label,
        })),
      },
    ];
  }, [customerInfo]);

  if (!customerInfo) return null;

  // No switcher for direct customers (and directly logged-in sub-accounts),
  // but still surface the account identity + display ID — it's what customers
  // read to support and see nowhere else in the header.
  if (!canAccessSubAccounts) {
    if (selectedCId == null) return null;
    return (
      // minWidth 0 + truncate: in the nowrap phone header this block must
      // shrink (long labels ellipsize) instead of wrapping to extra lines
      // that overflow the fixed 60px bar. No effect while space is ample.
      <div style={{ textAlign: 'right', minWidth: 0 }}>
        <Text size="sm" fw={500} lh={1.3} truncate>
          {selectedCustomer?.cfg.label}
        </Text>
        <Text size="xs" c="dimmed" ff="monospace" lh={1.3}>
          ID: {bcInt.getStr(selectedCId)}
        </Text>
      </div>
    );
  }

  return (
    <div>
      <Select
        data={data}
        value={selectedCId != null ? String(selectedCId) : null}
        onChange={handleChange}
        searchable
        filter={optionsFilter}
        size="sm"
        w={{ base: 190, sm: 220, lg: 260 }}
        comboboxProps={{ width: 320, shadow: 'md' }}
        placeholder="Select account"
        maxDropdownHeight={320}
        limit={100}
        nothingFoundMessage="No accounts found"
      />
      {selectedCId != null && (
        <Text size="xs" c="dimmed" mt={2} ff="monospace">
          ID: {bcInt.getStr(selectedCId)}
        </Text>
      )}
    </div>
  );
}
